/**
 * Browser-side image cut-outs. Removes a flat white/transparent backdrop by
 * flood-filling from the image edges (so white details inside the subject are
 * kept), feathers the edge, and crops to the subject.
 */

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

type Bounds = { x: number; y: number; w: number; h: number };

const WHITE_THRESHOLD = 228;
const FEATHER_FLOOR = 170;

function isBackdrop(d: Uint8ClampedArray, i: number): boolean {
  if (d[i + 3] < 16) return true;
  const r = d[i];
  const g = d[i + 1];
  const b = d[i + 2];
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  return min >= WHITE_THRESHOLD && max - min <= 26;
}

function removeBackdrop(data: ImageData): Uint8Array {
  const { width: w, height: h, data: d } = data;
  const mask = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let top = 0;

  const push = (p: number) => {
    if (mask[p]) return;
    if (!isBackdrop(d, p * 4)) return;
    mask[p] = 1;
    stack[top++] = p;
  };

  for (let x = 0; x < w; x++) {
    push(x);
    push((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    push(y * w);
    push(y * w + w - 1);
  }

  while (top > 0) {
    const p = stack[--top];
    const x = p % w;
    if (x > 0) push(p - 1);
    if (x < w - 1) push(p + 1);
    if (p >= w) push(p - w);
    if (p < w * (h - 1)) push(p + w);
  }

  for (let p = 0; p < w * h; p++) {
    const i = p * 4;
    if (mask[p]) {
      d[i + 3] = 0;
      continue;
    }
    const x = p % w;
    const touchesBackdrop =
      (x > 0 && mask[p - 1]) ||
      (x < w - 1 && mask[p + 1]) ||
      (p >= w && mask[p - w]) ||
      (p < w * (h - 1) && mask[p + w]);
    if (touchesBackdrop) {
      const min = Math.min(d[i], d[i + 1], d[i + 2]);
      const whiteness = Math.min(1, Math.max(0, (min - FEATHER_FLOOR) / (WHITE_THRESHOLD - FEATHER_FLOOR)));
      d[i + 3] = Math.round(d[i + 3] * (1 - whiteness * 0.9));
    }
  }
  return mask;
}

function findBounds(data: ImageData): Bounds {
  const { width: w, height: h, data: d } = data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] > 10) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { x: 0, y: 0, w, h };
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function readPixels(img: HTMLImageElement) {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(img, 0, 0);
  // Throws a SecurityError if the image is cross-origin without CORS.
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { canvas, ctx, data };
}

function exportCropped(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, data: ImageData): string {
  ctx.putImageData(data, 0, 0);
  const b = findBounds(data);
  const out = document.createElement("canvas");
  out.width = b.w;
  out.height = b.h;
  const outCtx = out.getContext("2d");
  if (!outCtx) throw new Error("Canvas unavailable");
  outCtx.drawImage(canvas, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
  return out.toDataURL("image/png");
}

/** Cuts a subject photographed on a white backdrop out of its background. */
export async function cutOutSubject(src: string): Promise<string> {
  const img = await loadImage(src);
  const { canvas, ctx, data } = readPixels(img);
  removeBackdrop(data);
  return exportCropped(canvas, ctx, data);
}

/**
 * Cuts a subject shot on a solid magenta (#FF00FF) chroma-key backdrop.
 * Magenta never appears on a yellow/black/steel grader, so the key is clean,
 * and see-through areas (cab glass) are keyed and de-spilled too.
 */
export async function cutOutChromaKey(src: string): Promise<string> {
  const img = await loadImage(src);
  const { canvas, ctx, data } = readPixels(img);
  const d = data.data;
  const KEY_FULL = 105;
  const KEY_NONE = 30;

  for (let i = 0; i < d.length; i += 4) {
    const r = d[i];
    const g = d[i + 1];
    const b = d[i + 2];
    const spill = Math.min(r, b) - g;
    if (spill <= KEY_NONE) continue;
    if (spill >= KEY_FULL) {
      d[i + 3] = 0;
      continue;
    }
    const keep = 1 - (spill - KEY_NONE) / (KEY_FULL - KEY_NONE);
    d[i + 3] = Math.round(d[i + 3] * keep);
    // Remove the magenta tint from semi-transparent edges and glass.
    d[i] = r - spill;
    d[i + 2] = b - spill;
  }

  return exportCropped(canvas, ctx, data);
}

/**
 * Removes the solid yellow backdrop from the Liftoff logo artwork, keeping
 * every logo colour (white, red, black) untouched. Anti-aliased edges are
 * un-mixed from the yellow so no yellow fringe is left on the charcoal page.
 */
export async function removeYellowBackground(src: string): Promise<string> {
  const img = await loadImage(src);
  const { canvas, ctx, data } = readPixels(img);
  const d = data.data;

  // Sample the backdrop colour from the top-left corner.
  const kr = d[0];
  const kg = d[1];
  const kb = d[2];
  const keyYellowness = Math.max(1, Math.min(kr, kg) - kb);
  const lo = keyYellowness * 0.12;
  const hi = keyYellowness * 0.85;

  for (let i = 0; i < d.length; i += 4) {
    const r = d[i];
    const g = d[i + 1];
    const b = d[i + 2];
    const yellowness = Math.min(r, g) - b;
    if (yellowness <= lo) continue;
    if (yellowness >= hi) {
      d[i + 3] = 0;
      continue;
    }
    const alpha = 1 - (yellowness - lo) / (hi - lo);
    const bg = 1 - alpha;
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
    d[i] = clamp((r - bg * kr) / alpha);
    d[i + 1] = clamp((g - bg * kg) / alpha);
    d[i + 2] = clamp((b - bg * kb) / alpha);
    d[i + 3] = Math.round(d[i + 3] * alpha);
  }

  return exportCropped(canvas, ctx, data);
}
